import { yupResolver } from '@hookform/resolvers/yup';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { FormAutocomplete, FormTextField } from '../../common/form';
import { ContactDto, CreateProductDto } from '../../common/PricePrgClient';
import { ProgressButton } from '../../common/ProgressButton';
import { RootState } from '../../store';
import { createProduct, showCreateProductDialog } from './redux/productSlices';

const mapStateToProps = (state: RootState) => ({
    contacts: state.contacts.contacts
});

type CreateProductForm = Omit<CreateProductDto, 'purchaser' | 'warehouseIds' | 'contactId'> & {
    contactId?: number | null;
};
const schema: yup.SchemaOf<CreateProductForm> = yup.object().shape({
    productId: yup.number().required().label('Product ID'),
    name: yup.string().required().max(60).label('Name'),
    conversionFactor: yup
        .number()
        .transform((_, value) => {
            if (value.includes('.')) {
                return null;
            }
            return +value.replace(/,/, '.');
        })
        .positive()
        .required()
        .label('Conversion factor'),
    countryOfOrigin: yup.string().required().label('Country of origin'),
    grossWeight: yup
        .number()
        .transform((_, value) => {
            if (value.includes('.')) {
                return null;
            }
            return +value.replace(/,/, '.');
        })
        .positive()
        .required()
        .label('Gross weight'),
    netWeight: yup
        .number()
        .transform((_, value) => {
            if (value.includes('.')) {
                return null;
            }
            return +value.replace(/,/, '.');
        })
        .positive()
        .required()
        .label('Net weight'),
    priceUnit: yup.string().required().oneOf(['ST', 'KG', 'COL']),
    contactId: yup.number().nullable().optional().label('Purchaser'),
    vat: yup.number()
});

const defaultValues = {
    name: '',
    productId: 0,
    priceUnit: 'ST'
} as CreateProductForm;

type Props = ReturnType<typeof mapStateToProps> & {
    parentCustomerId?: number;
};

const CreateProductDialog = ({ contacts }: Props) => {
    const dispatch = useDispatch();
    const [isSending, setIsSending] = useState(false);
    const { handleSubmit, control } = useForm<CreateProductForm>({
        resolver: yupResolver(schema),
        defaultValues: { ...defaultValues }
    });
    const [contactList, setContactList] = useState([] as ContactDto[]);

    const submitForm = (form: CreateProductForm) => {
        setIsSending(true);
        dispatch(
            createProduct.request({ ...form, contactId: form.contactId == null ? undefined : form.contactId })
        );
    };

    useEffect(() => {
        setContactList(Object.values(contacts));
    }, [contacts]);

    const handleClose = () => {
        dispatch(showCreateProductDialog(false));
    };

    return (
        <Dialog open={true} maxWidth="lg" PaperProps={{ sx: { width: '30%' } }}>
            <DialogTitle>Create new product</DialogTitle>
            <DialogContent>
                <form id="product-form" onSubmit={handleSubmit(submitForm)}>
                    <Grid container direction="row" spacing={2}>
                        <Grid item xs={12}>
                            <FormTextField
                                sx={{ my: 1 }}
                                label="Product ID"
                                control={control}
                                name="productId"
                                variant="standard"
                                autoComplete="off"
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid item xs={12}>
                            <FormTextField
                                name="name"
                                sx={{ my: 1 }}
                                fullWidth
                                control={control}
                                label="Name"
                                variant="standard"
                            />
                        </Grid>
                    </Grid>
                    <Grid direction="row" sx={{ my: 1 }}>
                        <Grid item xs={12} md={12}>
                            <FormControl fullWidth>
                                <InputLabel id="priceUnitLabel">Price unit</InputLabel>
                                <Controller
                                    control={control}
                                    name="priceUnit"
                                    render={({ field, fieldState: { error } }) => (
                                        <Select
                                            labelId="priceUnitLabel"
                                            label="Price unit"
                                            {...field}
                                            error={!!error}
                                        >
                                            <MenuItem value="ST">ST</MenuItem>
                                            <MenuItem value="KG">KG</MenuItem>
                                            <MenuItem value="COL">COL</MenuItem>
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={12} sx={{ my: 1 }}>
                            <FormTextField
                                control={control}
                                name="countryOfOrigin"
                                label="Country"
                                variant="standard"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={12} sx={{ my: 1 }}>
                            <FormTextField
                                name="conversionFactor"
                                control={control}
                                label="Conversion factor"
                                variant="standard"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={12} sx={{ my: 1 }}>
                            <FormTextField
                                name="grossWeight"
                                control={control}
                                label="Gross weight"
                                variant="standard"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={12} sx={{ my: 2 }}>
                            <FormTextField
                                name="netWeight"
                                control={control}
                                label="Net weight"
                                variant="standard"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={12} sx={{ my: 2 }}>
                            <FormTextField
                                fullWidth
                                name="vat"
                                control={control}
                                label="VAT"
                                variant="standard"
                            />
                        </Grid>
                        <Grid item xs={12} md={12} sx={{ my: 2 }}>
                            <FormAutocomplete
                                control={control}
                                name="contactId"
                                label="Purchaser"
                                options={contactList.map(c => c.contactId)}
                                getOptionLabel={c => contacts[c]?.fullName ?? ''}
                                defaultValue={null}
                            />
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <ProgressButton color="primary" type="submit" form="product-form" isLoading={isSending}>
                    Send
                </ProgressButton>
            </DialogActions>
        </Dialog>
    );
};

export default connect(mapStateToProps)(CreateProductDialog);
